import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, Users } from "lucide-react";

import { cn } from "../../lib/utils";
import { toStudents } from "../../lib/pricingTiers";

// The one input the whole pricing page turns on: a school types its student count and every card
// above the fold becomes its own price. Quick picks exist because a head who is "about 500" should
// not have to decide before they can see a number.
const QUICK_PICKS = [200, 500, 1000, 2000];

export default function KnowYourPrice({ model, copy = {}, onChange, initialStudents = 0 }) {
  const [raw, setRaw] = useState(initialStudents ? String(initialStudents) : "");
  const terms = useMemo(() => model?.terms || [], [model]);
  const [termKey, setTermKey] = useState("yearly");
  // Longer prepay terms are REVEALED, not shown by default. A first-time visitor is deciding
  // whether this is affordable at all; offering a 3-year commitment in that first breath is
  // asking a question they have not reached yet. Monthly and yearly answer "how much", and the
  // longer terms are one click away for the school that is ready to ask "how much if I commit".
  const [showLongTerms, setShowLongTerms] = useState(false);
  const [error, setError] = useState("");
  const fieldId = useId();
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;

  const students = toStudents(raw);
  const term = useMemo(
    () => terms.find((t) => t.key === termKey) || terms[0],
    [terms, termKey],
  );
  // The ladder is SERVED, so which terms exist is a platform decision, not a constant here. The
  // split is by duration, not by name: any term of a year or less is a headline choice, anything
  // longer is a prepay commitment. A new 5-year term appears in the reveal the day it is priced.
  const headlineTerms = useMemo(() => terms.filter((t) => (t.months || 1) <= 12), [terms]);
  const longTerms = useMemo(() => terms.filter((t) => (t.months || 1) > 12), [terms]);
  const visibleTerms = showLongTerms ? [...headlineTerms, ...longTerms] : headlineTerms;

  // THE DEFAULT FOLLOWS THE DATA UNTIL THE VISITOR CHOOSES.
  //
  // The first render happens before the price book has loaded, so a term resolved then is resolved
  // against a one-entry fallback ladder — which is how this page came to open on MONTHLY, the
  // SURCHARGED option, for every visitor: the fallback's only entry is the monthly baseline, and
  // the choice made against it was never revisited when the real ladder arrived a moment later.
  //
  // Re-resolving "when the key is missing from the new ladder" does not fix it, because `monthly`
  // is present in both. The condition has to be whether a PERSON has chosen — so the correction
  // runs only while the control is untouched, and a visitor's own click is never overridden.
  const termTouched = useRef(false);
  useEffect(() => {
    if (termTouched.current || !terms.length) return;
    const preferred = (terms.find((t) => t.key === "yearly") || terms[0]).key;
    if (preferred !== termKey) setTermKey(preferred);
  }, [terms, termKey]);
  const chooseTerm = useCallback((key) => {
    termTouched.current = true;
    setTermKey(key);
  }, []);

  useEffect(() => { onChange?.({ students, termKey }); }, [students, termKey, onChange]);

  const handleType = useCallback((value) => {
    setRaw(value);
    // Refuse, never rewrite: a field that silently replaces what someone typed is a field they
    // stop trusting, and the number here decides the price they are quoted.
    setError(
      value && !/^\d{1,7}$/.test(String(value).trim())
        ? "Enter the number of students as digits, for example 650."
        : "",
    );
  }, []);

  return (
    // THE CONSOLE. Deep brand navy with the logo orange as its live accent — deliberately NOT the
    // amber the first sketch reached for. Amber is not unreadable (ink on amber-300 measures
    // 12.4:1), so this is not an accessibility refusal; it is a MEANING one. On this very page
    // `caution` amber already says "Coming soon" and "Prerequisite:" in the comparison table one
    // scroll below, so dressing the page's most important control in it teaches a reader that the
    // colour means "careful" and then asks them to type into it.
    //
    // Navy earns the attention a different way, and a stronger one: it is the ONLY dark element
    // above the fold, so it becomes the focal point by contrast with the pale canvas and the
    // saturated cards beneath rather than by shouting. It also carries both logo colours — navy
    // ground, orange accent — and gives the most headroom of anything measured: white on it is
    // 16.9:1 and the logo orange on it is 6.0:1, both comfortably AA.
    <div className="relative mx-auto max-w-[34rem] overflow-hidden rounded-[26px] border border-white/10 bg-[linear-gradient(155deg,#101F45_0%,#0B1730_55%,#0A1428_100%)] p-6 sm:p-7 text-left shadow-[0_28px_70px_-28px_rgba(13,27,61,0.65)]">
      {/* A single warm bloom in the corner, so the panel reads as lit rather than as a black box.
          Decorative and inert — it carries no information and is hidden from assistive tech. */}
      <span aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-orange/25 blur-3xl" />
      <label htmlFor={fieldId} className="relative flex items-center gap-2 text-[14px] font-semibold text-white">
        <Users className="h-4 w-4 text-orange" aria-hidden="true" />
        {copy.students_label || "How many students do you have?"}
      </label>
      <div className="relative mt-3 flex items-center gap-2 rounded-2xl bg-white/[0.07] p-1.5 ring-1 ring-white/20 focus-within:ring-2 focus-within:ring-orange transition-[box-shadow] duration-200">
        {/* `type="text"` with a numeric inputMode, deliberately NOT `type="number"`. Three
            reasons, and the first is what makes the validation below real at all: a number input
            DISCARDS a non-numeric keystroke before React sees it, so the refusal message could
            never fire and the branch was dead code. It also puts a scroll-wheel handler on the
            field — a head scrolling past this control would silently change the price they are
            being quoted — and it renders spinners nobody wants on a roster count. */}
        <input
          id={fieldId}
          data-testid="kyp-students"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={7}
          value={raw}
          onChange={(e) => handleType(e.target.value)}
          placeholder="650"
          aria-describedby={error ? `${helpId} ${errorId}` : helpId}
          aria-invalid={error ? "true" : undefined}
          className="w-full bg-transparent px-4 py-3 text-[30px] font-heading font-bold tabular-nums text-white outline-none placeholder:font-normal placeholder:text-white/40"
        />
        <span className="shrink-0 pr-4 text-[15px] text-white/70">students</span>
      </div>
      <p id={helpId} className="relative mt-2 text-[13px] text-white/65">
        {copy.students_help || "Active students on your roll. An estimate is fine."}
      </p>
      {error ? (
        <p id={errorId} role="alert" data-testid="kyp-students-error"
           className="relative mt-1.5 text-[13px] font-medium text-orange">{error}</p>
      ) : null}

      <div className="relative mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Common school sizes">
        {QUICK_PICKS.map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`kyp-quick-${n}`}
            aria-pressed={students === n}
            onClick={() => handleType(String(n))}
            className={cn(
              "rounded-full border px-3 py-1.5 text-[13px] font-medium tabular-nums transition-[color,background-color,border-color] duration-150",
              students === n
                ? "border-orange bg-orange text-white"
                : "border-white/20 bg-white/[0.06] text-white/75 hover:border-orange/60 hover:text-white",
            )}
          >
            {n.toLocaleString("en-IN")}
          </button>
        ))}
      </div>

      {/* THE TERM CONTROL. Monthly and yearly are the headline choice; longer prepay terms are
          revealed, and the split is computed from each term's own months so a newly configured
          term lands on the right side without a code change.

          Native radios in a fieldset — arrow-key navigation, one tab stop and "2 of 2" position
          announcements, all free. A plan that does not sell the chosen term says so on its OWN
          card rather than the toggle hiding the option: a school comparing Growth with Pro needs
          to see that monthly exists AND that Pro does not offer it, which a missing control
          cannot say. */}
      <fieldset className="relative mt-6">
        <legend className="text-[13px] font-semibold text-white">How would you like to pay?</legend>
        <div className="mt-2.5 inline-flex flex-wrap items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] p-1.5">
          {visibleTerms.map((t) => {
            const active = t.key === termKey;
            const saving = Number(t.discount_bps) > 0;
            return (
              <React.Fragment key={t.key}>
                <input type="radio" id={`kyp-term-input-${t.key}`} name="kypTerm" value={t.key}
                       checked={active} onChange={() => chooseTerm(t.key)} className="sr-only peer" />
                <label
                  htmlFor={`kyp-term-input-${t.key}`}
                  data-testid={`kyp-term-${t.key}`}
                  className={cn(
                    "inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-semibold",
                    "transition-[color,background-color,box-shadow] duration-200",
                    "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-orange/70",
                    active
                      ? "bg-orange text-royal-deep shadow-[0_6px_18px_-6px_rgba(249,115,22,0.7)]"
                      : "text-white/75 hover:bg-white/10 hover:text-white",
                  )}
                >
                  {t.label}
                  {/* The saving rides INSIDE the label so it becomes part of that option's
                      accessible name ("2 years, save 8%") rather than a detached badge a
                      screen-reader user never hears next to the choice it belongs to. */}
                  {saving ? (
                    <span className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10.5px] font-bold leading-none",
                      active ? "bg-white/25 text-white" : "bg-orange/20 text-orange",
                    )}>
                      save {Math.round(t.discount_bps / 100)}%
                    </span>
                  ) : null}
                </label>
              </React.Fragment>
            );
          })}
        </div>
        {longTerms.length > 0 && !showLongTerms ? (
          <button
            type="button"
            data-testid="kyp-longer-terms"
            onClick={() => setShowLongTerms(true)}
            className="mt-3 inline-flex items-center gap-1 rounded text-[13px] font-semibold text-orange hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange/70"
          >
            Pay for longer and save more
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </fieldset>
      <p className="relative mt-3 text-[13px] text-white/70" data-testid="kyp-term-note">
        {term?.discount_bps > 0
          ? `Paying for ${term.months} months up front saves ${Math.round(term.discount_bps / 100)}%.`
          : term?.discount_bps < 0
            ? `Paying monthly costs ${Math.round(-term.discount_bps / 100)}% more than paying for the year.`
            : "Billed once for the year."}
      </p>
    </div>
  );
}
